/**
 * referralDashboardViewModel — `(reduxState, rawCode, options) → props de <ReferralDashboardPage>`.
 *
 * Patron B : ni `getReferralCode` ni `showReferralCodeByHash` n'ont de cas SUCCESS dans un réducteur
 * (`data/redux/reducers/data/referrals.js` n'en connaît que les quatre écrans d'administration), donc
 * la réponse n'atteint jamais `state.data`. L'écran la garde en local (`useReferralDashboardData`) et
 * la passe ici ; `loading` appartient au crochet de données.
 *
 * `hash` sert de la même façon que `studentEmail` dans `liveSessionsListViewModel` : il désigne la
 * tranche api qui porte l'erreur, parce que le legacy choisit l'appel sur la présence du hash
 * (`ReferralCodeScreen.js:78-85`) et affiche l'erreur de l'une ou de l'autre (`:196-203`).
 */
import { createSelector } from "reselect"

import {
    toReferralReferrerLabel,
    toUIReferralDashboard,
} from "../../../adapters/parent/Referral/referralDashboardAdapter"
import { type ReferralCodePayload } from "../../../ui/types/referralCode"
import { type UIReferralDashboard } from "../../../ui/types"

export type ReferralDashboardViewModelOptions = {
    /** Présent ⇒ la page a été chargée par `showReferralCodeByHash`. */
    hash?: string | null
    /**
     * Afficher l'identité du parrain. Réservé à la vue ouverte par lien : sur l'entrée parent, la
     * page montrerait au parent son propre nom et son propre e-mail comme une fiche d'admin.
     */
    showReferrerIdentity?: boolean
}

export type ReferralDashboardViewModel = {
    dashboard: UIReferralDashboard | null
    referrerLabel: string | null
    /** Erreur bloquante : seulement quand il n'y a rien à montrer. */
    error: unknown | null
}

/** Le store de l'app est en JS non typé — la frontière reste explicite. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

export const referralDashboardViewModel = createSelector(
    [
        (state: ReduxState) => state?.api?.showReferralCodeByHash,
        (state: ReduxState) => state?.api?.getReferralCode,
        (_state: ReduxState, raw: ReferralCodePayload | null) => raw,
        // Les deux options sont lues UNE À UNE : l'écran compose son littéral à chaque rendu, et
        // comparer l'objet remettrait le view-model au travail à chaque action.
        (_state: ReduxState, _raw: ReferralCodePayload | null, options?: ReferralDashboardViewModelOptions) =>
            options?.hash ?? null,
        (_state: ReduxState, _raw: ReferralCodePayload | null, options?: ReferralDashboardViewModelOptions) =>
            options?.showReferrerIdentity ?? false,
    ],
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    (byHashReq: any, mineReq: any, raw, hash, showReferrerIdentity): ReferralDashboardViewModel => {
        const req = hash ? byHashReq : mineReq
        const dashboard = toUIReferralDashboard(raw)

        return {
            dashboard,
            referrerLabel: showReferrerIdentity ? toReferralReferrerLabel(raw) : null,
            error: dashboard != null ? null : (req?.error ?? null),
        }
    }
)
